import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button, COLLAPSE, cn, Icons } from '@workflow/ui'
import { Markdown } from '@/features/announcements/Markdown'

/** Wraps the selection in a mark, or a placeholder when nothing is selected. */
function surround(area: HTMLTextAreaElement, before: string, after: string, placeholder: string): string {
  const { selectionStart: start, selectionEnd: end, value } = area
  const chosen = value.slice(start, end) || placeholder
  requestAnimationFrame(() => {
    area.focus()
    area.setSelectionRange(start + before.length, start + before.length + chosen.length)
  })
  return value.slice(0, start) + before + chosen + after + value.slice(end)
}

/** Starts each selected line with a prefix — headings and lists. */
function prefixLines(area: HTMLTextAreaElement, prefix: string): string {
  const { selectionStart: start, selectionEnd: end, value } = area
  const lineStart = value.lastIndexOf('\n', start - 1) + 1
  const block = value.slice(lineStart, end)
  requestAnimationFrame(() => area.focus())
  return value.slice(0, lineStart) + block.split('\n').map((line) => prefix + line).join('\n') + value.slice(Math.max(end, lineStart))
}

/** Puts text at the caret on its own line. */
function insertBlock(area: HTMLTextAreaElement, text: string): string {
  const { selectionStart: start, selectionEnd: end, value } = area
  const before = value.slice(0, start)
  const lead = before === '' || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n'
  const next = `${before}${lead}${text}\n\n${value.slice(end)}`
  const caret = before.length + lead.length + text.length + 2
  requestAnimationFrame(() => {
    area.focus()
    area.setSelectionRange(caret, caret)
  })
  return next
}

const isUrl = (text: string): boolean => /^https?:\/\/\S+$/i.test(text.trim())

/** One quiet mark in the writing toolbar. */
export function Tool({ label, keys, onClick, pressed, children }: { label: string; keys?: string; onClick: () => void; pressed?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      title={keys ? `${label} (${keys})` : label}
      aria-label={label}
      aria-pressed={pressed}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-fast outline-none hover:bg-tint hover:text-foreground focus-visible:ring-1 focus-visible:ring-foreground',
        pressed && 'bg-selected text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Writing on a page, not in a form — the surface announcements and notes
 * share: the words with nothing drawn around them, growing as they go, and
 * one quiet row of marks at the foot. A pasted address over selected words
 * links them; the picture mark takes an image's address and sets it on its
 * own line. Ctrl+B, Ctrl+I and Ctrl+K mark the text; Ctrl+Enter submits.
 *
 * It renders three stacked parts for a flex-column holder: the scrolling
 * page (with `header` above the words), the panels that open above the
 * toolbar (`panels`, then the address line), and the toolbar itself, with
 * `tools` after the shared marks and `actions` at its far end.
 */
export function WritingSurface({
  value,
  onChange,
  onSubmit,
  label,
  placeholder,
  maxLength,
  areaRef,
  header,
  panels,
  tools,
  actions,
  minHeight = 'min-h-[14rem]',
  autoFocus = false,
}: {
  value: string
  onChange: (next: string) => void
  onSubmit: () => void
  label: string
  placeholder: string
  maxLength: number
  areaRef?: RefObject<HTMLTextAreaElement | null>
  header?: ReactNode
  panels?: ReactNode
  tools?: ReactNode
  actions: ReactNode
  minHeight?: string
  /** Take the caret once the holding dialog has finished rising. */
  autoFocus?: boolean
}) {
  const [preview, setPreview] = useState(false)
  const [asking, setAsking] = useState<'link' | 'image' | null>(null)
  const [address, setAddress] = useState('')
  const ownRef = useRef<HTMLTextAreaElement | null>(null)
  const area = areaRef ?? ownRef

  useEffect(() => {
    if (!autoFocus) return
    const timer = setTimeout(() => area.current?.focus(), 60)
    return () => clearTimeout(timer)
  }, [autoFocus, area])

  // The page grows with the writing; the holder scrolls once it is full.
  useLayoutEffect(() => {
    const el = area.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value, preview, area])

  const mark = (apply: (el: HTMLTextAreaElement) => string) => {
    if (preview) setPreview(false)
    requestAnimationFrame(() => {
      if (area.current) onChange(apply(area.current))
    })
  }
  const ask = (kind: 'link' | 'image') => {
    setPreview(false)
    setAsking(kind)
  }
  const addAddress = () => {
    const url = address.trim()
    if (!isUrl(url) || !area.current) return
    const el = area.current
    const selected = el.value.slice(el.selectionStart, el.selectionEnd)
    onChange(asking === 'image'
      ? insertBlock(el, url)
      : selected ? surround(el, '[', `](${url})`, selected) : insertBlock(el, url))
    setAsking(null)
    setAddress('')
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-3 pb-6 sm:px-8">
        {header}
        {preview ? (
          <div className={cn('mt-4', minHeight)}>
            {value.trim()
              ? <Markdown source={value} className="text-ui leading-relaxed text-foreground" />
              : <p className="text-ui text-muted-foreground/60">Nothing to preview yet.</p>}
          </div>
        ) : (
          <textarea
            ref={area}
            aria-label={label}
            placeholder={placeholder}
            value={value}
            maxLength={maxLength}
            onChange={(event) => onChange(event.target.value)}
            onPaste={(event) => {
              const text = event.clipboardData.getData('text/plain')
              const el = event.currentTarget
              if (isUrl(text) && el.selectionStart !== el.selectionEnd) {
                event.preventDefault()
                onChange(surround(el, '[', `](${text.trim()})`, ''))
              }
            }}
            onKeyDown={(event) => {
              const mod = event.ctrlKey || event.metaKey
              if (mod && event.key === 'Enter') { event.preventDefault(); onSubmit() }
              else if (mod && event.key.toLowerCase() === 'b') { event.preventDefault(); onChange(surround(event.currentTarget, '**', '**', 'bold')) }
              else if (mod && event.key.toLowerCase() === 'i') { event.preventDefault(); onChange(surround(event.currentTarget, '*', '*', 'italic')) }
              else if (mod && event.key.toLowerCase() === 'k') { event.preventDefault(); setAsking('link') }
            }}
            className={cn(
              'block w-full resize-none overflow-hidden bg-transparent text-ui leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/50',
              header ? 'mt-4' : 'mt-1',
              minHeight,
            )}
          />
        )}
      </div>

      <AnimatePresence initial={false}>
        {panels}
        {asking ? (
          <motion.div key="address" {...COLLAPSE} className="overflow-hidden">
            <div className="flex items-center gap-2 border-t border-hairline px-6 py-2.5 sm:px-8">
              <span className="text-muted-foreground" aria-hidden>{asking === 'image' ? <Icons.image /> : <Icons.link />}</span>
              <input
                aria-label={asking === 'image' ? 'Image address' : 'Link address'}
                placeholder={asking === 'image' ? 'Paste an image link' : 'Paste a link'}
                value={address}
                autoFocus
                onChange={(event) => setAddress(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); addAddress() }
                  if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setAsking(null) }
                }}
                className="min-w-0 flex-1 bg-transparent text-ui text-foreground outline-none placeholder:text-muted-foreground/60"
              />
              <Button size="xs" variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
              <Button size="xs" variant="primary" disabled={!isUrl(address)} onClick={addAddress}>Add</Button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="flex items-center gap-1 border-t border-hairline px-4 py-2.5 sm:px-6" role="toolbar" aria-label="Formatting">
        <Tool label="Bold" keys="Ctrl+B" onClick={() => mark((el) => surround(el, '**', '**', 'bold'))}><Icons.bold /></Tool>
        <Tool label="Italic" keys="Ctrl+I" onClick={() => mark((el) => surround(el, '*', '*', 'italic'))}><Icons.italic /></Tool>
        <Tool label="Heading" onClick={() => mark((el) => prefixLines(el, '## '))}><Icons.heading /></Tool>
        <Tool label="List" onClick={() => mark((el) => prefixLines(el, '- '))}><Icons.list /></Tool>
        <Tool label="Link" keys="Ctrl+K" onClick={() => ask('link')}><Icons.link /></Tool>
        <Tool label="Image" onClick={() => ask('image')}><Icons.image /></Tool>
        {tools}
        <span className="mx-1 h-4 w-px bg-hairline" aria-hidden />
        <Tool label="Preview" pressed={preview} onClick={() => setPreview((current) => !current)}><Icons.review /></Tool>
        <span className="flex-1" />
        {actions}
      </div>
    </>
  )
}
